import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import logo from "../../icons/landinglogo.svg";
import { Link, animateScroll as scroll } from "react-scroll";
import ContactUs from "../ContactUs";

const DEFAULT_NAV_LINKS = [
  { to: "hero", label: "Home", number: "01" },
  { to: "why-bellhopt", label: "Why Bellhopt", number: "02" },
  { to: "integrations", label: "Integrations", number: "03" },
  { to: "how-it-works", label: "How It Works", number: "04" },
  { to: "service-areas", label: "Servicing Areas", number: "05" },
  { to: "testimonials", label: "Social Proof", number: "06" },
  { to: "faq", label: "FAQ", number: "07" },
];

function ResponsiveNavbar({ navLinks = DEFAULT_NAV_LINKS, useHomeHashLinks = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const closeContactModal = () => {
    setShowContactModal(false);
  };

  const scrollToTop = () => {
    scroll.scrollToTop({
      duration: 500,
      smooth: true,
    });
    setIsOpen(false);
  };

  const handleNavClick = () => {
    setIsOpen(false);
  };

  return (
    <>
      <ContactUs
        showModal={showContactModal}
        handleCloseModal={closeContactModal}
        requireSupportType
      />

      {/* Mobile Header Bar - Liquid Glass */}
      <nav className="w-full desktopSpecial:hidden fixed top-0 left-0 right-0 z-50 flex justify-center items-start pointer-events-none">
        <div
          className="
            relative
            w-[92%] max-w-6xl mx-auto mt-4
            flex items-center justify-between
            px-5 py-3.5
            rounded-[999px]
            pointer-events-auto
            overflow-hidden
          "
        >
          {/* Liquid Glass Base Layer */}
          <div
            className="
              absolute inset-0
              bg-white/35
              backdrop-blur-[60px]
              -webkit-backdrop-blur-[60px]
              rounded-[999px]
              border border-[#E5E7EB]/80
              shadow-[0_8px_40px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)]
            "
          />
          
          {/* Outer border for definition */}
          <div
            className="
              absolute inset-0
              rounded-[999px]
              border border-[#D1D5DB]/60
              pointer-events-none
            "
          />
          
          {/* Glass Reflection Layer */}
          <div
            className="
              absolute inset-0
              bg-gradient-to-b from-white/60 via-white/20 to-transparent
              rounded-[999px]
              pointer-events-none
            "
          />
          
          {/* Inner Glass Highlight */}
          <div
            className="
              absolute inset-[1px]
              rounded-[999px]
              border border-white/70
              pointer-events-none
            "
          />
          
          {/* Subtle gray tint for visibility */}
          <div
            className="
              absolute inset-0
              bg-gradient-to-br from-[#F9FAFB]/30 via-transparent to-transparent
              rounded-[999px]
              pointer-events-none
            "
          />
          
          {/* Content Container */}
          <div className="relative z-10 flex items-center justify-between w-full gap-3">
            {/* Logo */}
            {useHomeHashLinks ? (
              <RouterLink
                to="/"
                className="flex items-center gap-2 focus:outline-none relative z-10 flex-shrink-0"
              >
                <img
                  src={logo}
                  alt="Bellhopt"
                  className="h-8 w-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.1)]"
                />
              </RouterLink>
            ) : (
              <button
                type="button"
                onClick={scrollToTop}
                className="flex items-center gap-2 focus:outline-none relative z-10 flex-shrink-0"
              >
                <img
                  src={logo}
                  alt="Bellhopt"
                  className="h-8 w-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.1)]"
                />
              </button>
            )}

            {/* Liquid Glass Hamburger Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="
                relative
                w-10 h-10
                flex flex-col
                items-center justify-center
                gap-1.5
                rounded-full
                overflow-hidden
                transition-all duration-200
                focus:outline-none
                group
                flex-shrink-0
              "
              aria-label="Toggle menu"
            >
              {/* Glass Button Base */}
              <div
                className="
                  absolute inset-0
                  bg-white/25
                  backdrop-blur-[20px]
                  -webkit-backdrop-blur-[20px]
                  rounded-full
                  border border-white/40
                  shadow-[0_4px_16px_rgba(0,0,0,0.06)]
                  group-hover:bg-white/35
                  transition-all duration-200
                "
              />
              
              {/* Button Reflection */}
              <div
                className="
                  absolute inset-0
                  bg-gradient-to-br from-white/50 via-transparent to-transparent
                  rounded-full
                  pointer-events-none
                "
              />
              
              {/* Hamburger Lines */}
              <span
                className={`
                  relative z-10
                  w-5 h-[1.5px]
                  rounded-full
                  transition-all duration-300
                  ${isOpen ? "rotate-45 translate-y-2 bg-[#111827]" : "bg-[#374151]"}
                  drop-shadow-[0_1px_2px_rgba(255,255,255,0.5)]
                `}
              />
              <span
                className={`
                  relative z-10
                  w-5 h-[1.5px]
                  rounded-full
                  transition-all duration-300
                  ${isOpen ? "opacity-0" : "opacity-100 bg-[#374151]"}
                  drop-shadow-[0_1px_2px_rgba(255,255,255,0.5)]
                `}
              />
              <span
                className={`
                  relative z-10
                  w-5 h-[1.5px]
                  rounded-full
                  transition-all duration-300
                  ${isOpen ? "-rotate-45 -translate-y-2 bg-[#111827]" : "bg-[#374151]"}
                  drop-shadow-[0_1px_2px_rgba(255,255,255,0.5)]
                `}
              />
            </button>
          </div>
        </div>
      </nav>

      {/* Full-Screen Liquid Glass Menu Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 flex flex-col items-center justify-center desktopSpecial:hidden overflow-hidden"
          onClick={() => setIsOpen(false)}
        >
          {/* Liquid Glass Base Layer */}
          <div
            className="
              absolute inset-0
              bg-white/35
              backdrop-blur-[60px]
              -webkit-backdrop-blur-[60px]
            "
          />
          
          {/* Outer border for definition */}
          <div
            className="
              absolute inset-0
              border border-[#D1D5DB]/60
              pointer-events-none
            "
          />
          
          {/* Glass Reflection Layer */}
          <div
            className="
              absolute inset-0
              bg-gradient-to-b from-white/60 via-white/20 to-transparent
              pointer-events-none
            "
          />
          
          {/* Inner Glass Highlight */}
          <div
            className="
              absolute inset-[1px]
              border border-white/70
              pointer-events-none
            "
          />
          
          {/* Subtle gray tint for visibility */}
          <div
            className="
              absolute inset-0
              bg-gradient-to-br from-[#F9FAFB]/30 via-transparent to-transparent
              pointer-events-none
            "
          />
          
          {/* Content Container */}
          <div 
            className="relative z-10 flex flex-col items-center justify-center w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="
                absolute top-6 right-6
                w-12 h-12
                flex items-center justify-center
                rounded-full
                bg-white/25
                backdrop-blur-[20px]
                -webkit-backdrop-blur-[20px]
                border border-white/40
                shadow-[0_4px_16px_rgba(0,0,0,0.06)]
                hover:bg-white/35
                hover:border-white/60
                transition-all duration-200
                text-[28px] font-light text-[#111827]
                hover:text-[#6B7280]
              "
              onClick={() => setIsOpen(false)}
              aria-label="Close menu"
            >
              {/* Button Reflection */}
              <div
                className="
                  absolute inset-0
                  bg-gradient-to-br from-white/50 via-transparent to-transparent
                  rounded-full
                  pointer-events-none
                "
              />
              <span className="relative z-10">×</span>
            </button>

            {/* Centered Navigation */}
            <nav className="flex flex-col items-center gap-6">
              {navLinks.map((link, index) => {
                const menuClassName = `
                    relative
                    text-[14px] 
                    tracking-[0.20em] 
                    uppercase 
                    text-[#111827]
                    hover:text-[#FF4D4D] 
                    transition-colors
                    cursor-pointer
                    opacity-0
                  `;
                const menuStyle = {
                  animation: isOpen ? `fadeIn 0.4s ease-out ${index * 80}ms forwards` : "none",
                };
                return useHomeHashLinks ? (
                  <RouterLink
                    key={link.to}
                    to={`/#${link.to}`}
                    onClick={handleNavClick}
                    className={menuClassName}
                    style={menuStyle}
                  >
                    {link.number} {link.label}
                  </RouterLink>
                ) : (
                  <Link
                    key={link.to}
                    to={link.to}
                    smooth={true}
                    duration={500}
                    offset={-80}
                    onClick={handleNavClick}
                    className={menuClassName}
                    style={menuStyle}
                  >
                    {link.number} {link.label}
                  </Link>
                );
              })}
            </nav>

            <div className="mt-10 flex flex-col items-center gap-3">
              <a
                href="https://host.bellhopt.com/login"
                onClick={() => setIsOpen(false)}
                className="
                  px-6 py-3
                  rounded-full
                  border border-[#FF4D4D33]
                  text-[#FF4D4D]
                  text-[12px]
                  font-semibold
                  tracking-[0.08em]
                  uppercase
                  hover:bg-[#FF4D4D]
                  hover:text-white
                  transition-colors
                "
              >
                Host login
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Add CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
}

export default ResponsiveNavbar;
